/**
 * WatchlistPanel — TradingView-style token watchlist with named group tabs.
 *
 * Groups are owned by App (wlStore); this panel switches/renames/adds/deletes
 * groups and renders the active group's tokens. tokenWatchlist /
 * setTokenWatchlist still address only the ACTIVE group so the chart star +
 * enrichment keep working unchanged.
 *
 * KIND-AWARE: each group mixes sol tokens and futures pairs. Futures rows
 * use kind:'fut' and a stable pseudo-address (`fut:<coin>`).
 *
 * Props:
 *   tokenWatchlist, setTokenWatchlist  — active group tokens
 *   wlGroups, wlActiveId               — [{id,name,tokens}] + active id
 *   onSelectWlGroup(id), onAddWlGroup(), onRenameWlGroup(id,name),
 *   onDeleteWlGroup(id)
 *   onSelect (addressOrFutKey => void), currentToken
 *   maxPerGroup (default 100)
 */
import { useState, useRef, useEffect } from 'react'
import { cmeInfo } from '../utils/chartUtils'

function fmtMcap(n) {
  if (!n) return '—'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
  return `$${Math.round(n)}`
}

const SECTION_STYLE = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  opacity: 0.85,
  padding: '6px 8px 3px',
  marginBottom: 2,
}

export default function WatchlistPanel({
  tokenWatchlist = [],
  setTokenWatchlist,
  wlGroups = [],
  wlActiveId,
  onSelectWlGroup,
  onAddWlGroup,
  onRenameWlGroup,
  onDeleteWlGroup,
  onSelect,
  currentToken,
  maxPerGroup = 100,
}) {
  const sol = tokenWatchlist.filter(t => t.kind !== 'fut')
  const fut = tokenWatchlist.filter(t => t.kind === 'fut')
  const [renamingId, setRenamingId] = useState(null)
  const [renameDraft, setRenameDraft] = useState('')
  const renameRef = useRef(null)

  useEffect(() => {
    if (renamingId && renameRef.current) {
      renameRef.current.focus()
      renameRef.current.select()
    }
  }, [renamingId])

  const startRename = (g) => {
    setRenamingId(g.id)
    setRenameDraft(g.name || '')
  }

  const commitRename = () => {
    if (!renamingId) return
    const name = renameDraft.trim().slice(0, 24)
    if (name) onRenameWlGroup?.(renamingId, name)
    setRenamingId(null)
  }

  const row = (t) => {
    const isFut = t.kind === 'fut'
    const sym = isFut
      ? (t.symbol || t.name || t.coin)
      : (t.symbol || t.name || `${t.address.slice(0, 4)}…${t.address.slice(-4)}`)
    const ch = t.stats24h?.priceChange
    const hasCh = typeof ch === 'number'
    const chCls = !hasCh ? 'flat' : ch > 0 ? 'up' : ch < 0 ? 'down' : 'flat'
    const isActive = currentToken && t.address === currentToken
    return (
      <div
        key={t.address}
        className={`wl-row${isActive ? ' active' : ''}`}
        onClick={() => onSelect?.(t.address)}
        title={isFut ? `${t.coin} ${cmeInfo(t.coin) ? 'CME future' : 'perp'}` : t.address}
      >
        <span className="wl-col wl-col-sym">{sym}</span>
        <span className="wl-col wl-col-mcap">{isFut ? (cmeInfo(t.coin) ? 'CME' : 'PERP') : fmtMcap(t.mcap || t.fdv)}</span>
        <span className={`wl-col wl-col-chg ${chCls}`}>
          {hasCh ? `${ch > 0 ? '+' : ''}${ch.toFixed(1)}%` : '—'}
        </span>
        <button
          className="wl-remove"
          title="Remove"
          onClick={(e) => {
            e.stopPropagation()
            setTokenWatchlist(prev => prev.filter(w => w.address !== t.address))
          }}
        >×</button>
      </div>
    )
  }

  const count = tokenWatchlist.length
  const atCap = count >= maxPerGroup

  return (
    <div className="wl-panel">
      <div className="wl-group-tabs" role="tablist">
        {wlGroups.map(g => {
          const active = g.id === wlActiveId
          const n = Array.isArray(g.tokens) ? g.tokens.length : 0
          if (renamingId === g.id) {
            return (
              <form
                key={g.id}
                className={`wl-group-tab renaming${active ? ' active' : ''}`}
                onSubmit={(e) => { e.preventDefault(); commitRename() }}
              >
                <input
                  ref={renameRef}
                  className="wl-group-rename"
                  value={renameDraft}
                  maxLength={24}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => { if (e.key === 'Escape') setRenamingId(null) }}
                />
              </form>
            )
          }
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`wl-group-tab${active ? ' active' : ''}`}
              onClick={() => onSelectWlGroup?.(g.id)}
              onDoubleClick={(e) => { e.preventDefault(); startRename(g) }}
              title={`${g.name} (${n}) — double-click to rename`}
            >
              <span className="wl-group-tab-name">{g.name}</span>
              <span className="wl-group-tab-count">{n}</span>
              {wlGroups.length > 1 && (
                <span
                  className="wl-group-tab-x"
                  title="Delete group"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (n > 0 && !window.confirm(`Delete "${g.name}" (${n} tokens)?`)) return
                    onDeleteWlGroup?.(g.id)
                  }}
                >×</span>
              )}
            </button>
          )
        })}
        <button
          type="button"
          className="wl-group-tab wl-group-add"
          title="New watchlist group"
          onClick={() => {
            const id = onAddWlGroup?.()
            // Parent returns new id; kick rename on next tick once tabs render
            if (id) setTimeout(() => {
              setRenamingId(id)
              setRenameDraft('New')
            }, 0)
          }}
        >+</button>
      </div>

      <div className="wl-panel-header">
        <span className="wl-col wl-col-sym">Symbol</span>
        <span className="wl-col wl-col-mcap">MCap</span>
        <span className="wl-col wl-col-chg">24h</span>
        <span className={`wl-cap${atCap ? ' full' : ''}`} title="Tokens in this group">
          {count}/{maxPerGroup}
        </span>
      </div>
      <div className="wl-rows">
        {sol.length === 0 && fut.length === 0 ? (
          <div className="wl-empty">No tokens watched — add with ☆ from the chart’s right-click menu</div>
        ) : (
          <>
            {sol.length > 0 && (
              <div style={{ ...SECTION_STYLE, borderBottom: '2px solid #b8860b' }}>Tokens</div>
            )}
            {sol.map(row)}
            {fut.length > 0 && (
              <div style={{ ...SECTION_STYLE, borderBottom: '2px solid #3b82f6' }}>Futures</div>
            )}
            {fut.map(row)}
          </>
        )}
      </div>
    </div>
  )
}
